import { ProCard } from '@components'
import cn from '@integrations/classnames'
import type { HTMLAttributes, InputHTMLAttributes, MouseEvent, ReactNode } from '@integrations/react'
import styles from './FilterBar.module.scss'

/** The card that holds a list page's filters, laid out as wrapping rows of `FilterPill`s. */
function FilterBar({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <ProCard className={cn('filter-bar', className)} {...props}>
      {children}
    </ProCard>
  )
}

function Row({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(styles['filter-bar__row'], 'filter-bar__row', className)} {...props}>
      {children}
    </div>
  )
}

/** Pushes its content to the end of a `FilterBar.Row` (e.g. Clear filters). */
function End({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(styles['filter-bar__end'], 'filter-bar__end', className)} {...props}>
      {children}
    </div>
  )
}

interface FilterPillProps {
  label: ReactNode
  /** Tints the pill once the filter narrows the results. */
  active?: boolean
  /** Canvas-filled pill for a free-text field (a search box, a reference, an ID typed in). */
  search?: boolean
  className?: string
  children: ReactNode
  'data-test'?: string
}

/**
 * A 36px pill with an inline muted label followed by its control. The control may be a native
 * `FilterPill.Input` or a library `Input`/selector; their frames are reset by their own skins'
 * `.filter-pill` context so the pill is the only frame.
 */
export function FilterPill({ label, active = false, search = false, className, children, ...props }: FilterPillProps) {
  // A label only focuses the select inside it, which doesn't open its menu; react-select opens on a
  // mousedown on its control, so forward the press there (and let it toggle like a click on the control).
  function openSelect(event: MouseEvent<HTMLLabelElement>) {
    const control = event.currentTarget.querySelector('.select-input__control')

    if (control && !control.contains(event.target as Node)) {
      event.preventDefault()
      control.dispatchEvent(new window.MouseEvent('mousedown', { bubbles: true, cancelable: true, button: 0 }))
    }
  }

  return (
    <label
      onMouseDown={openSelect}
      className={cn(styles['filter-pill'], 'filter-pill', className, {
        [styles['filter-pill--search']]: search,
        'filter-pill--search': search,
        [styles['active']]: active,
        active
      })}
      {...props}
    >
      <span className={cn(styles['filter-pill__label'], 'filter-pill__label')}>{label}</span>
      {children}
    </label>
  )
}

function PillInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(styles['filter-pill__input'], 'filter-pill__input', className)} {...props} />
}

FilterPill.Input = PillInput

FilterBar.Row = Row
FilterBar.End = End

export default FilterBar
